import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, CalendarCheck, Globe } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage, LANGS } from "../i18n";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/servizi", label: "Servizi" },
  { to: "/siti-web", label: "Siti web" },
  { to: "/consulenze", label: "Consulenze" },
  { to: "/chi-siamo", label: "Chi siamo" },
  { to: "/blog", label: "Blog" },
  { to: "/contatti", label: "Contatti" },
];

export const LangSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center gap-0.5 rounded-full border border-salvia/40 bg-white/60 p-1 backdrop-blur" data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          data-testid={`lang-${l.code}`}
          onClick={() => setLang(l.code)}
          aria-label={`Lingua: ${l.label}`}
          className={`rounded-full px-2.5 py-1 text-[0.7rem] font-bold tracking-wide transition-colors duration-300 ${
            lang === l.code ? "bg-petrolio text-avorio" : "text-antracite/55 hover:text-petrolio"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled || open
          ? "border-b border-salvia/30 bg-avorio/85 shadow-sm backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
        <Link to="/" aria-label="Alsera Studio — Home">
          <Logo />
        </Link>

        <ul className="hidden items-center gap-6 lg:flex">
          {LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors duration-300 hover:text-petrolio ${
                    isActive ? "text-petrolio" : "text-antracite/70"
                  }`
                }
              >
                {t(l.label)}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LangSwitcher />
          </div>
          <Link
            to="/prenota"
            data-testid="nav-booking-cta"
            className="btn-primary hidden !px-6 !py-2.5 lg:inline-flex"
          >
            <CalendarCheck className="h-4 w-4" />
            {t("Prenota una consulenza")}
          </Link>
          <button
            type="button"
            data-testid="nav-hamburger"
            aria-label={open ? t("Chiudi menu") : t("Apri menu")}
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-salvia/50 bg-white/70 p-2.5 text-petrolio backdrop-blur transition-colors hover:bg-acqua lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-salvia/20 bg-avorio/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <ul className="space-y-1 px-5 py-4">
              {LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                        isActive ? "bg-acqua text-petrolio" : "text-antracite/75 hover:bg-acqua/50"
                      }`
                    }
                  >
                    {t(l.label)}
                  </NavLink>
                </li>
              ))}
              <li className="pt-2">
                <Link to="/prenota" data-testid="mobile-nav-booking-cta" className="btn-primary w-full">
                  <CalendarCheck className="h-4 w-4" />
                  {t("Prenota una consulenza")}
                </Link>
              </li>
              <li className="flex justify-center pt-3">
                <LangSwitcher />
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
